import { memo, useCallback, useEffect, useState } from 'react';
import { Form, Key } from 'react-aria-components';

import {
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  ConfirmDialog,
  Flex,
  FlexProps,
  Heading,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  PillSelectValue,
  Popover,
  Select,
  SelectItem,
  SelectPills,
  SelectPillsOption,
  Text,
  TextField,
} from '@/components/core';
import { ManagedRole } from '@/constants';
import { toRoleId, useCreateRole, useDeleteRole, useGetRole, useListAgents, useUpdateRole } from '@/hooks/api';
import { AccessLevel, createPermissions, getAccessLevel, Id } from '@/utils';

import { ArrowIcon, CheckmarkFilledIcon, SaveIcon, WarningDuotoneIcon } from '../icons';
import { merge } from '../utils';

export type AddOrEditRoleProps = FlexProps & {
  roleId?: string | null;
  close?: () => void;
};

interface ItemType extends Id {
  name?: string | null;
}

const SelectableListBoxItem = <T extends ItemType>({ value, ...rest }: ListBoxItemProps<T>) => {
  return (
    <ListBoxItem value={value} id={value?.id} {...rest} className="group">
      <Flex className="gap-sm items-center">
        <CheckmarkFilledIcon className="text-primary-500 not-group-data-selected:hidden" />
        <Text>{value?.name}</Text>
      </Flex>
    </ListBoxItem>
  );
};

const IAddOrEditRole = ({ roleId, close, className, ...rest }: AddOrEditRoleProps) => {
  const { data: agents } = useListAgents();
  const { data: role } = useGetRole(toRoleId(roleId));

  const { mutateAsync: createRole, isPending: isPendingCreate } = useCreateRole();
  const { mutateAsync: updateRole, isPending: isPendingUpdate } = useUpdateRole();
  const { mutateAsync: deleteRole, isPending: isPendingDelete } = useDeleteRole();
  const isPendingCreateOrUpdate = isPendingCreate || isPendingUpdate;

  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>();
  const [selectedAgents, setSelectedAgents] = useState<Key[]>();

  const onCreateOrUpdateRole = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = Object.fromEntries(new FormData(e.currentTarget));

      if (accessLevel && selectedAgents) {
        const permissions = createPermissions(
          accessLevel?.toString() as AccessLevel,
          selectedAgents?.map((k) => k.toString())
        );

        if (!roleId) {
          await createRole({ ...formData, permissions });
        } else {
          await updateRole({
            id: toRoleId(role?.roleId),
            updates: { ...formData, permissions },
          });
        }

        close?.();
      }
    },
    [accessLevel, close, createRole, role?.roleId, roleId, selectedAgents, updateRole]
  );

  const onDeleteRole = useCallback(() => {
    if (role) {
      deleteRole(toRoleId(role.roleId)).then(close);
    }
  }, [close, deleteRole, role]);

  const isManagedRole = role?.roleId != null && Object.values(ManagedRole).includes(role.roleId as ManagedRole);

  useEffect(() => {
    const permissions = role?.permissions;

    if (permissions) {
      const agentIds = permissions.filter((p) => p.resourceId.startsWith('cradl:agent')).map((p) => p.resourceId);
      const allAgents = agents?.data?.map((a) => a.agentId);

      const accessLevel = getAccessLevel(permissions);
      setAccessLevel(accessLevel);
      setSelectedAgents(isManagedRole ? allAgents : agentIds);
    }
  }, [agents?.data, isManagedRole, role?.permissions]);

  const isExistingRole = !!roleId;

  return (
    <>
      <CardHeader variant="plain" className="gap-xs items-center">
        <Button onPress={close}>
          <ArrowIcon rotation="left" />
        </Button>
        <Heading>{isExistingRole ? 'Edit role' : 'New role'}</Heading>
      </CardHeader>

      <Form onSubmit={onCreateOrUpdateRole}>
        <CardBody variant="plain" col className="gap-sm">
          {isManagedRole && (
            <Flex variant="warning" className="p-sm gap-xs items-center rounded-md">
              <WarningDuotoneIcon />
              <Text size="xs">This is a managed role. Managed roles cannot be edited or deleted.</Text>
            </Flex>
          )}

          <Flex col className={merge('gap-sm', className)} {...rest}>
            <TextField name="name" defaultValue={role?.name || ''} isDisabled={isManagedRole}>
              <Label isRequired>Role name</Label>
              <Input placeholder="My role" />
            </TextField>

            <TextField name="description" defaultValue={role?.description || ''} isDisabled={isManagedRole}>
              <Label>Description</Label>
              <Input placeholder="An optional description of the role." />
            </TextField>

            <SelectPills
              key={selectedAgents?.toString()}
              onChange={setSelectedAgents}
              defaultValue={selectedAgents}
              isDisabled={isManagedRole}
              label="Agents"
              isRequired
              items={agents?.data.map((a) => ({ ...a, id: a.agentId }))}
              itemRenderFn={(item) => <SelectItem>{item?.name}</SelectItem>}
              optionRenderFn={(option) => (
                <SelectPillsOption value={option}>
                  <Flex className="gap-xs items-center">
                    <Text>{option?.name}</Text>
                  </Flex>
                </SelectPillsOption>
              )}
            />

            <Select
              key={accessLevel?.toString()}
              onChange={(key) => setAccessLevel(key?.toString() as AccessLevel)}
              defaultValue={accessLevel}
              isDisabled={isManagedRole}
            >
              <Flex col variant="plain" className="gap-xs">
                <Label isRequired>Access Level</Label>
                <Button className="bg-inherit hover:bg-inherit">
                  <PillSelectValue>
                    {(item?: ItemType | null) => (
                      <Text size="xs" variant="default" className="p-xs rounded-md border-1 border-gray-200 bg-gray-50">
                        {item?.name}
                      </Text>
                    )}
                  </PillSelectValue>
                </Button>
              </Flex>
              <Popover>
                <ListBox
                  className="max-h-[20rem] overflow-auto"
                  items={Object.entries(AccessLevel).map(([key, val]) => ({ id: val, name: key.toString() }))}
                >
                  {(item) => <SelectableListBoxItem value={item} />}
                </ListBox>
              </Popover>
            </Select>
          </Flex>
        </CardBody>

        <CardFooter variant="plain" separator>
          <Flex className="gap-sm w-full justify-end">
            {role?.roleId ? (
              <>
                <ConfirmDialog onConfirm={onDeleteRole} confirm="This role will be permanently deleted.">
                  <Button variant="danger" isDisabled={isPendingDelete || isManagedRole}>
                    {isPendingDelete && <SaveIcon />}
                    <Text>Delete role</Text>
                  </Button>
                </ConfirmDialog>
                <div className="grow" />
              </>
            ) : null}

            <Button variant="secondary" onPress={close}>
              <Text>Cancel</Text>
            </Button>

            <Button type="submit" variant="primary" isDisabled={isPendingCreateOrUpdate || isManagedRole}>
              {isPendingCreateOrUpdate && <SaveIcon />}
              <Text>Save</Text>
            </Button>
          </Flex>
        </CardFooter>
      </Form>
    </>
  );
};

export const AddOrEditRole = memo(IAddOrEditRole);
